import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StartGameOverlay from './StartGameOverlay';
import RetroLoadingDots from './RetroLoadingDots';

// Simple pixel art patterns to recreate
const PATTERNS = [
  {
    name: 'Heart',
    grid: [
      [0,1,1,0,1,1,0],
      [1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1],
      [0,1,1,1,1,1,0],
      [0,0,1,1,1,0,0],
      [0,0,0,1,0,0,0],
    ],
    color: '#FF0080'
  },
  {
    name: 'Star',
    grid: [
      [0,0,0,1,0,0,0],
      [0,0,1,1,1,0,0],
      [0,1,1,1,1,1,0],
      [1,1,1,1,1,1,1],
      [0,1,1,1,1,1,0],
      [0,0,1,1,1,0,0],
      [0,0,0,1,0,0,0],
    ],
    color: '#FFD700'
  },
  {
    name: 'Smile',
    grid: [
      [0,0,1,1,1,0,0],
      [0,1,0,0,0,1,0],
      [1,0,1,0,1,0,1],
      [1,0,0,0,0,0,1],
      [1,0,1,1,1,0,1],
      [0,1,0,0,0,1,0],
      [0,0,1,1,1,0,0],
    ],
    color: '#00FF00'
  },
  {
    name: 'Mushroom',
    grid: [
      [0,0,1,1,1,0,0],
      [0,1,1,1,1,1,0],
      [1,1,0,1,0,1,1],
      [0,1,1,1,1,1,0],
      [0,0,0,1,0,0,0],
      [0,0,1,1,1,0,0],
      [0,0,0,1,0,0,0],
    ],
    color: '#FF4D00'
  },
  {
    name: 'Ghost',
    grid: [
      [0,0,1,1,1,0,0],
      [0,1,1,1,1,1,0],
      [1,1,0,1,0,1,1],
      [1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1],
      [1,0,1,0,1,0,1],
      [1,0,1,0,1,0,1],
    ],
    color: '#00FFE0'
  }
];

const RetroPixelChallenge = ({ 
  isActive = false, 
  onEnd,
  onGameComplete,
  showStartButton = false,
  isGenerating = false 
}) => {
  const [currentPattern, setCurrentPattern] = useState(null);
  const [userGrid, setUserGrid] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [score, setScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  // Show start button only when explicitly requested (like in RetroArcade)
  const [waitingToStart, setWaitingToStart] = useState(showStartButton);
  const containerRef = useRef(null);

  // Reset game state when component becomes inactive
  useEffect(() => {
    if (!isActive) {
      setGameStarted(false);
      setWaitingToStart(showStartButton);
      setTimeLeft(10);
      setScore(0);
      setUserGrid([]);
      setCurrentPattern(null);
    }
  }, [isActive, showStartButton]);

  // Initialize game
  useEffect(() => {
    if (isActive && !gameStarted && !waitingToStart) {
      startNewGame();
    }
  }, [isActive, waitingToStart]);

  const startNewGame = () => {
    const pattern = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
    setCurrentPattern(pattern);
    setUserGrid(Array(pattern.grid.length).fill().map(() => 
      Array(pattern.grid[0].length).fill(0)
    ));
    setTimeLeft(10);
    setScore(0);
    setGameStarted(true);
  };

  const handleStart = () => {
    setWaitingToStart(false);
  };

  // Handle game end
  const handleGameEnd = () => {
    const finalScore = calculateFinalScore();
    setGameStarted(false);
    onGameComplete?.({
      type: 'pixelchallenge',
      score: finalScore,
      pattern: currentPattern?.name,
      title: 'Pixel Challenge Results',
      message: `You matched ${finalScore}% of the pattern!`
    });
    onEnd?.();
  };

  // Game timer - only runs when game has actually started
  useEffect(() => {
    if (!gameStarted || waitingToStart) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleGameEnd();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, waitingToStart, currentPattern]);

  const calculateFinalScore = () => {
    if (!currentPattern) return 0;

    let matches = 0;
    let total = 0;

    currentPattern.grid.forEach((row, i) => {
      row.forEach((cell, j) => {
        if (cell === 1) {
          total++;
          if (userGrid[i][j] === 1) matches++;
        }
      });
    });

    const accuracy = Math.round((matches / total) * 100);
    setScore(accuracy);
    return accuracy;
  };

  const handleDraw = (e) => {
    if (!gameStarted || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const gridSize = Math.min(rect.width, rect.height);
    const cellSize = gridSize / currentPattern.grid[0].length;

    const x = Math.floor((e.clientX - rect.left) / cellSize);
    const y = Math.floor((e.clientY - rect.top) / cellSize);

    if (x >= 0 && x < currentPattern.grid[0].length && 
        y >= 0 && y < currentPattern.grid.length) {
      setUserGrid(prev => {
        const newGrid = [...prev];
        newGrid[y][x] = 1;
        return newGrid;
      });
    }
  };

  return (
    <motion.div 
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={() => setIsDrawing(true)}
      onMouseUp={() => setIsDrawing(false)}
      onMouseLeave={() => setIsDrawing(false)}
      onMouseMove={(e) => isDrawing && handleDraw(e)}
      onClick={handleDraw}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <AnimatePresence>
        {waitingToStart ? (
          <StartGameOverlay 
            onStart={handleStart}
            gameName="Pixel Challenge"
          />
        ) : (
          <>
            {/* Timer and Score */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <div className="text-white font-['VT323'] text-3xl">
                {timeLeft}s
              </div>
              {score > 0 && (
                <div className="text-white font-['VT323'] text-3xl">
                  {score}% Match
                </div>
              )}
            </div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex gap-8">
                {/* Target Pattern */}
                <div className="relative">
                  <div className="text-white font-['VT323'] text-xl mb-2 text-center">
                    {currentPattern?.name || 'Target'}
                  </div>
                  <div className="grid gap-1" 
                    style={{ 
                      gridTemplateColumns: `repeat(${currentPattern?.grid[0].length}, 1fr)`,
                    }}
                  >
                    {currentPattern?.grid.map((row, i) => 
                      row.map((cell, j) => (
                        <motion.div
                          key={`target-${i}-${j}`}
                          className="w-6 h-6 rounded-sm"
                          style={{
                            backgroundColor: cell ? currentPattern.color : 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: (i + j) * 0.02 }}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* User Drawing */}
                <div className="relative">
                  <div className="text-white font-['VT323'] text-xl mb-2 text-center">Your Drawing</div>
                  <div className="grid gap-1"
                    style={{ 
                      gridTemplateColumns: `repeat(${currentPattern?.grid[0].length}, 1fr)`,
                    }}
                  >
                    {userGrid.map((row, i) => 
                      row.map((cell, j) => (
                        <motion.div
                          key={`user-${i}-${j}`}
                          className="w-6 h-6 rounded-sm cursor-pointer"
                          style={{
                            backgroundColor: cell ? currentPattern?.color : 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}
                          whileHover={{ scale: 1.1 }}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>


    </motion.div>
  );
};

export default RetroPixelChallenge; 